/**
 * VisualEditorModal — extracted from TeacherDashboard.js (Feb 2026 refactor).
 *
 * Lightbox the teacher opens from a visual section's "Open Visual Editor"
 * button (e.g. Maps & Geography). It lists the section's AI image prompts —
 * independent of each other — each with its own Generate Image button, and
 * shows the section's current and AI-generated images for quick removal.
 */
import React, { useState } from 'react';
import { X, Image, Sparkles, Trash2 } from 'lucide-react';
import { Button } from './../../ui/button';
import { MathText } from './../../RenderContent';
import { InlineSparkle } from '../../Loader';

function VisualEditorModal({ section, onClose, onGenerateImage, isGenerating, onRemoveSectionImage, onRemoveGeneratedImage }) {
  // Which prompt is generating right now — `isGenerating` from the parent is
  // one flag for the whole section, so this keeps "Generating..." on the
  // prompt actually clicked.
  const [generatingIdx, setGeneratingIdx] = useState(null);
  const sectionImages = section.section_images || [];
  const generatedImages = section.generated_images || [];
  const prompts = section.image_prompts || [];

  const generate = async (prompt, i) => {
    setGeneratingIdx(i);
    try { await onGenerateImage(prompt); } finally { setGeneratingIdx(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="visual-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-5 border-b border-slate-100" style={{ background: 'linear-gradient(90deg, #f0fdf4, #f0f9ff)' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#34A853]/10"><Image className="w-5 h-5 text-[#1A7F37]" /></div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{section.title}</h3>
            <p className="text-xs text-slate-500">Visual Aids — generate images from the prompts below</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100" aria-label="Close" data-testid="visual-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {sectionImages.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-3">Current Section Images ({sectionImages.length})</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {sectionImages.map((img, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-slate-200 shadow-sm group/img relative" data-testid={`sec-img-${i}`}>
                    <img src={img.url} alt={img.caption || ''} className="w-full aspect-video object-cover bg-slate-50" onError={e => { e.target.closest('[data-testid]').style.display = 'none'; }} />
                    {img.caption && <p className="text-xs text-slate-500 p-2 bg-slate-50">{img.caption}</p>}
                    <button onClick={() => onRemoveSectionImage(i)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity shadow-md hover:bg-red-600" data-testid={`delete-sec-img-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {generatedImages.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-3">AI Generated</p>
              <div className="grid grid-cols-2 gap-4">
                {generatedImages.map((img, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-lg transition-shadow relative group" data-testid={`gen-img-${section.id}-${i}`}>
                    <img src={img.url} alt="" className="w-full object-contain max-h-[350px] bg-slate-50" onError={e => { e.target.closest('[data-testid]').style.display = 'none'; }} />
                    <button onClick={() => onRemoveGeneratedImage(i)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600" data-testid={`delete-gen-img-${section.id}-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Each prompt stands on its own — a plain list, not a flowchart. */}
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-3">AI Image Prompts{prompts.length > 0 ? ` (${prompts.length})` : ''}</p>
            {prompts.length === 0 ? (
              <p className="text-xs text-slate-400 py-6 text-center rounded-xl border border-dashed border-slate-200" data-testid="no-image-prompts">
                This section has no image prompts yet.
              </p>
            ) : (
              <ol className="space-y-3" data-testid="image-prompt-list">
                {prompts.map((p, i) => (
                  <li key={i} className="flex items-center gap-4 rounded-xl border border-slate-200 p-4 hover:border-[#34A853]/40 transition-colors" data-testid={`image-prompt-${i}`}>
                    <span className="w-6 h-6 rounded-full bg-[#34A853]/10 text-[#1A7F37] text-xs font-semibold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      {/* Prompts often carry LaTeX ($\vec{F}=m\vec{a}$) — render it like the section text. */}
                      <p className="text-sm font-semibold text-slate-900"><MathText text={p.label || ''} /></p>
                      <p className="text-xs text-slate-500 leading-relaxed mt-1"><MathText text={p.prompt || ''} /></p>
                    </div>
                    <Button
                      onClick={() => generate(p.prompt, i)}
                      disabled={isGenerating}
                      className="h-9 px-4 rounded-xl text-white text-xs font-semibold flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #34A853, #0d9488)' }}
                      data-testid={`generate-prompt-${i}`}>
                      {generatingIdx === i
                        ? <InlineSparkle size={16} label="Generating image" className="mr-1.5" />
                        : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                      {generatingIdx === i ? 'Generating...' : 'Generate Image'}
                    </Button>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default VisualEditorModal;
